import React, { useState } from "react";
import firebase from "firebase/compat/app";
import { db } from "services/firebase";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormHelperText,
  TextField,
  Button,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";

const AccountSettings = ({ user, userData, visible, close, setSubmitted }) => {
  const { t } = useTranslation("account");
  // Get email from Firebase Auth first, fallback to Firestore userData
  const currentEmail = user?.email || userData?.email || "";

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm({
    defaultValues: {
      email: currentEmail,
    },
  });
  const [loading, setLoading] = useState(false);
  const [firebaseError, setFirebaseError] = useState();

  // Watch email to determine if changes were made
  const watchEmail = watch("email");

  const onSubmit = async (data) => {
    setLoading(true);
    setFirebaseError(null);
    setSubmitted(false);

    try {
      // Check if this is a new verification (no Auth email) or an update
      const isNewVerification = !user.email;

      // Only check for changes if user already has a verified email
      if (!isNewVerification && data.email === user.email) {
        setFirebaseError(t("emailSettings.noChanges"));
        setLoading(false);
        return;
      }

      try {
        // Configure action code settings with custom URL parameters
        const actionCodeSettings = {
          url: `${window.location.origin}/account/verify?uid=${
            user.uid
          }&pendingEmail=${encodeURIComponent(data.email)}`,
          handleCodeInApp: false, // Set to false for email action links
        };

        // Send verification email to the new address
        await user.verifyBeforeUpdateEmail(data.email, actionCodeSettings);

        // Store the pending email update in Firestore
        await db.collection("users").doc(user.uid).update({
          pendingEmail: data.email,
          emailChangeRequested: firebase.firestore.FieldValue.serverTimestamp(),
        });

        // Reset form and show success message
        reset({
          email: data.email, // Show the email they're verifying
        });

        setSubmitted(t("emailSettings.sent"));

        close();
      } catch (emailError) {
        console.error("Email update error:", emailError);

        switch (emailError.code) {
          case "auth/requires-recent-login":
            setFirebaseError(t("errors.recentLogin"));
            break;
          case "auth/email-already-in-use":
            setFirebaseError(t("emailSettings.emailInUse"));
            break;
          case "auth/invalid-email":
            setFirebaseError(t("emailSettings.invalidEmail"));
            break;
          default:
            setFirebaseError(t("errors.generic", { message: emailError.message }));
        }
      }
    } catch (error) {
      console.error("Unexpected error:", error);
      setFirebaseError(t("errors.unexpected", { message: error.message }));
    } finally {
      setLoading(false);
    }
  };

  // When dialog closes, reset the form
  const handleClose = () => {
    reset({
      email: currentEmail,
    });
    setFirebaseError(null);
    close();
  };

  // If user data not loaded yet, return loading screen
  if (!user) {
    return (
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={visible}
        onClick={close}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  return (
    <Dialog open={visible} onClose={handleClose}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>
          {user?.email ? t("emailSettings.titleUpdate") : t("emailSettings.titleVerify")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            {user?.email
              ? t("emailSettings.introUpdate")
              : t("emailSettings.introVerify")}
          </DialogContentText>

          <Controller
            name="email"
            control={control}
            defaultValue={currentEmail}
            rules={{
              required: t("validation.emailRequired"),
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                message: t("validation.emailInvalidShort"),
              },
            }}
            render={({ field }) => (
              <TextField
                id="email"
                label={t("common.email")}
                type="email"
                InputLabelProps={{ shrink: true }}
                variant="standard"
                fullWidth
                error={!!errors?.email}
                helperText={errors?.email?.message}
                sx={{ mb: 2 }}
                {...field}
              />
            )}
          />

          {firebaseError && (
            <FormHelperText error sx={{ mt: 1 }}>
              {firebaseError}
            </FormHelperText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t("common.close")}</Button>
          <LoadingButton
            loading={loading}
            type="submit"
            disabled={loading || (user?.email && watchEmail === user.email)}
            autoFocus
            variant="contained"
          >
            {user?.email ? t("emailSettings.submitUpdate") : t("emailSettings.submitVerify")}
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AccountSettings;
